import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ElementRef, ViewChild, QueryList, ViewChildren, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';

export interface DropdownOption {
  value: any;
  label: string;
}

@Component({
  selector: 'app-custom-dropdown',
  standalone: true,
  imports: [CommonModule,ReactiveFormsModule,FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDropdownComponent),
      multi: true
    }
  ],
  template: `
    <div class="relative inline-block w-full" [class]="className" #dropdownRef>
      <!-- Trigger Button -->
      <button
        type="button"
      (mousedown)="recordPointerInteraction()"
      (click)="toggleDropdown()"
      (keydown)="handleTriggerKeydown($event)"
      (focus)="handleTriggerFocus()"
        aria-haspopup="listbox"
        [attr.aria-expanded]="isOpen"
        [attr.aria-controls]="dropdownId"
        class="w-full bg-white border border-gray-300 rounded-lg px-4 pt-[9px] pb-[6px] text-left text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 transition-colors duration-200 flex items-center justify-between custom-dropdown-trigger"
        [class.disabled]="disabled"
        [attr.disabled]="disabled ? true : null"
        [attr.aria-disabled]="disabled"
      >
        <span class="text-sm font-normal truncate">
          {{ getDisplayText() }}
        </span>
        <svg 
          class="w-4 h-4 text-gray-500 transition-transform duration-200"
          [class.rotate-180]="isOpen"
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <polyline points="6,9 12,15 18,9"></polyline>
        </svg>
      </button>

      <!-- Dropdown Menu -->
      <div 
        *ngIf="isOpen" 
        [attr.id]="dropdownId"
        role="listbox"
        [attr.aria-multiselectable]="multiSelect"
        class="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto dropdown-menu"
      >
        <div *ngIf="searchable" class="px-4 py-2 border-b border-gray-200">
          <input
            type="text"
            [(ngModel)]="searchTerm"
            (ngModelChange)="onSearchChange($event)"
            placeholder="Search..."
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 dropdown-search-input"
          />
        </div>
        <div
          *ngFor="let option of visibleOptions; let i = index; trackBy: trackByFn"
          (click)="handleOptionClick(option)"
          class="px-4 py-3 cursor-pointer option transition-colors duration-150 flex items-center gap-3"
          [class]="getOptionClasses(option, i)"
          #optionItem
          role="option"
          tabindex="-1"
          [attr.aria-selected]="isSelected(option.value)"
        >
          <!-- Check  box for multi-select -->
          <div *ngIf="multiSelect" class="flex items-center">
            <div 
              class="w-4 h-4 border-2 rounded flex items-center justify-center transition-colors duration-200"
              [class]="getCheckboxClasses(option)"
            >
              <svg 
                *ngIf="isSelected(option.value)" 
                class="w-3 h-3 text-white" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <polyline points="20,6 9,17 4,12"></polyline>
              </svg>
            </div>
          </div>
          <span 
            class="text-sm font-normal flex-1 normal-case" 
            [class]="getOptionTextClasses(option)"
          >
            {{ normalizeLabel(option.label) }}
          </span>
        </div>
        
        <!-- No options message -->
        <div 
          *ngIf="visibleOptions.length === 0" 
          class="px-4 py-3 text-sm text-gray-500 text-center"
        >
          No options available
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
    
    .rotate-180 {
      transform: rotate(180deg);
    }
    .normal-case{
      text-transform: none;
    }
    .option:hover {
      background-color: #e2e8f0;
      color:black;
    }

    .custom-dropdown-trigger.disabled {
      cursor: not-allowed;
      pointer-events: none;
      opacity: 0.7;
    }

   :host-context(.dark) .option:hover {
      background-color: #333;
      color: #fff;
   }

   :host-context(.dark) .custom-dropdown-trigger {
      background-color: #1e1e2e;
      border-color: #444;
      color: #e0e0e0;
   }

   :host-context(.dark) .custom-dropdown-trigger:hover {
      border-color: #666;
   }

   :host-context(.dark) .dropdown-menu {
      background-color: #1e1e2e;
      border-color: #444;
   }

   :host-context(.dark) .dropdown-search-input {
      background-color: #2a2a3a;
      border-color: #444;
      color: #e0e0e0;
   }

   :host-context(.dark) .dropdown-search-input::placeholder {
      color: #888;
   }

   :host-context(.dark) .option {
      color: #e0e0e0;
   }
    
  `]
})
export class CustomDropdownComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() options: DropdownOption[] = [];
  @Input() placeholder: string = 'Select';
  @Input() multiSelect: boolean = false;
  @Input() className: string = '';
  @Input() disabled: boolean = false;
  @Input() searchable: boolean = true;
  @Input() preserveLabelCase: boolean = false;
  searchTerm: string = '';
  @Output() selectionChange = new EventEmitter<any>();

  @ViewChild('dropdownRef', { static: true }) dropdownRef!: ElementRef;
  @ViewChildren('optionItem') optionItems!: QueryList<ElementRef<HTMLElement>>;
  readonly dropdownId = `custom-dropdown-${Math.random().toString(36).slice(2, 10)}`;

  highlightedIndex: number = -1;

  isOpen: boolean = false;
  selectedValues: any = null;

  private clickListener?: (event: Event) => void;
  private documentKeyListener?: (event: KeyboardEvent) => void;
  private onChange = (value: any) => {};
  private onTouched = () => {};
  private lastDocumentKey: string | null = null;

  ngOnInit() {
    this.setupClickOutsideListener();
    this.setupDocumentKeyListener();
    
    // Initialize selectedValues based on multiSelect mode
    if (this.multiSelect && !Array.isArray(this.selectedValues)) {
      this.selectedValues = [];
    }
  }

  ngOnDestroy() {
    this.removeClickOutsideListener();
    this.removeDocumentKeyListener();
  }
  
  get visibleOptions(): DropdownOption[] {
    if (!this.options?.length) {
      return [];
    }
    if (!this.searchable) {
      return this.options;
    }
    if (!this.searchTerm?.trim()) {
      return this.options;
    }
    const term = this.searchTerm.toLowerCase();
    return this.options.filter(opt => opt.label?.toLowerCase().includes(term));
  }
  private setupClickOutsideListener() {
    this.clickListener = (event: Event) => {
      if (this.dropdownRef?.nativeElement && 
          !this.dropdownRef.nativeElement.contains(event.target as Node)) {
        this.closeDropdown();
      }
    };
    document.addEventListener('mousedown', this.clickListener);
  }

  private removeClickOutsideListener() {
    if (this.clickListener) {
      document.removeEventListener('mousedown', this.clickListener);
    }
  }

  toggleDropdown() {
    if (this.disabled) {
      return;
    }
    this.isOpen ? this.closeDropdown() : this.openDropdown();
  }

  lastInteraction: 'keyboard' | 'pointer' | null = null;

  handleTriggerKeydown(event: KeyboardEvent) {
    if (this.disabled) return;

    this.lastInteraction = 'keyboard';

    const key = event.key;

    if (key === 'Tab') {
      if (!this.isOpen) {
        event.preventDefault();
        this.openDropdown();
      } else {
        this.closeDropdown();
      }
      return;
    }

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      if (this.isOpen) {
        this.selectHighlightedOption();
      } else {
        this.openDropdown();
      }
      return;
    }

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      if (!this.isOpen) {
        this.openDropdown();
      }
      this.moveHighlight(key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if (key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.closeDropdown();
    }
  }

  handleTriggerFocus() {
    if (this.disabled) return;
    if (this.lastInteraction === 'pointer') {
      return;
    }

    if (!this.isOpen && (this.lastDocumentKey === 'Tab' || this.lastInteraction === 'keyboard')) {
      this.openDropdown();
      this.lastDocumentKey = null;
    }
  }

  recordPointerInteraction(): void {
    this.lastInteraction = 'pointer';
  }

  handleOptionClick(option: DropdownOption) {
    if (this.disabled) return;

    const visible = this.visibleOptions;
    const optionIndex = visible.findIndex(item => item.value === option.value);
    this.highlightedIndex = optionIndex;

    if (this.multiSelect) {
      const currentValues = Array.isArray(this.selectedValues) ? this.selectedValues : [];
      const newSelectedValues = currentValues.includes(option.value)
        ? currentValues.filter(val => val !== option.value)
        : [...currentValues, option.value];
      
      this.selectedValues = newSelectedValues;
      this.onChange(newSelectedValues);
      this.selectionChange.emit(newSelectedValues);
    } else {
      this.selectSingleOption(option);
    }
  }

  private openDropdown() {
    if (this.disabled) return;
    this.isOpen = true;
    this.onTouched();
    this.highlightedIndex = this.visibleOptions.length > 0 ? 0 : -1;
    this.scrollHighlightedIntoView();
  }

  private closeDropdown() {
    this.isOpen = false;
    this.highlightedIndex = -1;
  }

  private moveHighlight(step: number) {
    const options = this.visibleOptions;
    if (!options.length) {
      this.highlightedIndex = -1;
      return;
    }

    let nextIndex = this.highlightedIndex;
    if (nextIndex < 0) {
      nextIndex = step > 0 ? 0 : options.length - 1;
    } else {
      nextIndex += step;
    }
    if (nextIndex < 0) {
      nextIndex = options.length - 1;
    } else if (nextIndex >= options.length) {
      nextIndex = 0;
    }

    this.highlightedIndex = nextIndex;
    this.scrollHighlightedIntoView();

    if (!this.multiSelect) {
      this.selectSingleOption(options[nextIndex], false);
    }
  }

  private selectHighlightedOption() {
    const options = this.visibleOptions;
    if (this.highlightedIndex < 0 || this.highlightedIndex >= options.length) {
      return;
    }
    this.handleOptionClick(options[this.highlightedIndex]);
  }

  private selectSingleOption(option: DropdownOption, closeDropdown = true) {
    if (this.selectedValues !== option.value) {
      this.selectedValues = option.value;
      this.onChange(option.value);
      this.selectionChange.emit(option.value);
    }
    if (closeDropdown) {
      this.closeDropdown();
    }
  }

  private scrollHighlightedIntoView() {
    if (this.highlightedIndex < 0) return;
    setTimeout(() => {
      const optionArray = this.optionItems?.toArray();
      const highlightedEl = optionArray?.[this.highlightedIndex];
      highlightedEl?.nativeElement?.scrollIntoView({ block: 'nearest' });
    });
  }

  onSearchChange(value: string) {
    this.searchTerm = value;
    this.highlightedIndex = this.visibleOptions.length > 0 ? 0 : -1;
    this.scrollHighlightedIntoView();
  }

  private setupDocumentKeyListener() {
    if (typeof document === 'undefined') {
      return;
    }
    this.documentKeyListener = (event: KeyboardEvent) => {
      this.lastDocumentKey = event.key;
    };
    document.addEventListener('keydown', this.documentKeyListener, true);
  }

  private removeDocumentKeyListener() {
    if (this.documentKeyListener && typeof document !== 'undefined') {
      document.removeEventListener('keydown', this.documentKeyListener, true);
      this.documentKeyListener = undefined;
      this.lastDocumentKey = null;
    }
  }

  getDisplayText(): string {
    if (!Array.isArray(this.options)) {
      return this.placeholder;
    }
    if (this.multiSelect) {
      const values = Array.isArray(this.selectedValues) ? this.selectedValues : [];
      if (values.length === 0) return this.placeholder;
      if (values.length === 1) {
        const selected = this.options.find(opt => opt.value === values[0]);
        return selected ? this.normalizeLabel(selected.label) : this.placeholder;
      }
      return `${values.length} selected`;
    } else {
      const selected = this.options.find(opt => opt.value === this.selectedValues);
      return selected ? this.normalizeLabel(selected.label) : this.placeholder;
    }
  }

  normalizeLabel(value: string | undefined | null): string {
    if (!value) return '';
    if (this.preserveLabelCase) {
      return String(value);
    }
    return String(value)
      .split(' ')
      .map(word => {
        const normalized = word.trim();
        if (!normalized) return '';
        return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
      })
      .filter(Boolean)
      .join(' ');
  }

  isSelected(optionValue: any): boolean {
    return this.multiSelect 
      ? (Array.isArray(this.selectedValues) && this.selectedValues.includes(optionValue))
      : this.selectedValues === optionValue;
  }

  getOptionClasses(option: DropdownOption, index: number): string {
    let classes = '';
    const totalOptions = this.visibleOptions.length;
    if (index === 0) classes += ' rounded-t-lg';
    if (index === totalOptions - 1) {
      classes += ' rounded-b-lg';
    } else {
      classes += ' border-gray-100';
    }
    
    if (index === this.highlightedIndex) {
      classes += ' bg-gray-100 text-black';
    } else if (this.isSelected(option.value)) {
      classes += ' bg-gray-50';
    }
    
    return classes;
  }

  getCheckboxClasses(option: DropdownOption): string {
    return this.isSelected(option.value) 
      ? 'bg-gray-500 border-gray-500' 
      : 'border-gray-300 bg-white';
  }

  getOptionTextClasses(option: DropdownOption): string {
    return this.isSelected(option.value) 
      ? 'text-gray-700' 
      : '';
  }

  trackByFn(index: number, item: DropdownOption): any {
    return item.value;
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.selectedValues = this.multiSelect 
      ? (Array.isArray(value) ? value : [])
      : value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.disabled) {
      this.closeDropdown();
    }
  }
}

//                                 -------How to use -----
// Demo Component
// @Component({
//   selector: 'app-dropdown-demo',
//   standalone: true,
//   imports: [CommonModule, CustomDropdownComponent],
//   template: `
//     <div class="min-h-screen bg-gray-50 p-8">
//       <div class="max-w-4xl mx-auto space-y-8">
//         <div class="text-center">
//           <h1 class="text-3xl font-bold text-gray-900 mb-2">Custom Dropdown Component</h1>
//           <p class="text-gray-600">Reusable Angular dropdown with single and multi-select functionality</p>
//         </div>

//         <div class="grid md:grid-cols-2 gap-8">
//           <!-- Single Select -->
//           <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
//             <h2 class="text-xl font-semibold text-gray-900 mb-4">Single Select</h2>
//             <div class="space-y-4">
//               <div>
//                 <label class="block text-sm font-normal text-gray-700 mb-2">
//                   Device Make*
//                 </label>
//                 <app-custom-dropdown
//                   [options]="deviceOptions"
//                   placeholder="Select"
//                   [multiSelect]="false"
//                   (selectionChange)="onSingleSelectChange($event)"
//                 ></app-custom-dropdown>
//               </div>
//               <div class="text-sm text-gray-600">
//                 Selected: <span class="font-normal">{{ singleValue || 'None' }}</span>
//               </div>
//             </div>
//           </div>

//           <!-- Multi Select -->
//           <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
//             <h2 class="text-xl font-semibold text-gray-900 mb-4">Multi Select</h2>
//             <div class="space-y-4">
//               <div>
//                 <label class="block text-sm font-normal text-gray-700 mb-2">
//                   Device Make*
//                 </label>
//                 <app-custom-dropdown
//                   [options]="deviceOptions"
//                   placeholder="Select"
//                   [multiSelect]="true"
//                   (selectionChange)="onMultiSelectChange($event)"
//                 ></app-custom-dropdown>
//               </div>
//               <div class="text-sm text-gray-600">
//                 Selected: <span class="font-normal">
//                   {{ multiValues.length > 0 ? multiValues.join(', ') : 'None' }}
//                 </span>
//               </div>
//             </div>
//           </div>
//         </div>

//         <!-- Usage Examples -->
//         <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
//           <h2 class="text-xl font-semibold text-gray-900 mb-4">Usage Examples</h2>
//           <div class="space-y-4 text-sm">
//             <div class="bg-gray-50 p-4 rounded-lg">
//               <h3 class="font-normal text-gray-900 mb-2">Single Select:</h3>
//               <pre class="text-gray-700 text-xs overflow-x-auto">{{ singleSelectExample }}</pre>
//             </div>
//             <div class="bg-gray-50 p-4 rounded-lg">
//               <h3 class="font-normal text-gray-900 mb-2">Multi Select:</h3>
//               <pre class="text-gray-700 text-xs overflow-x-auto">{{ multiSelectExample }}</pre>
//             </div>
//             <div class="bg-gray-50 p-4 rounded-lg">
//               <h3 class="font-normal text-gray-900 mb-2">With Reactive Forms:</h3>
//               <pre class="text-gray-700 text-xs overflow-x-auto">{{ reactiveFormExample }}</pre>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   `
// })
// export class DropdownDemoComponent {
//   singleValue: any = null;
//   multiValues: any[] = [];

//   deviceOptions: DropdownOption[] = [
//     { value: 'apple', label: 'Apple' },
//     { value: 'mi', label: 'Mi' },
//     { value: 'oneplus', label: 'Oneplus' },
//     { value: 'poco', label: 'Poco' },
//     { value: 'realme', label: 'Realme' },
//     { value: 'samsung', label: 'Samsung' },
//     { value: 'oppo', label: 'Oppo' },
//     { value: 'vivo', label: 'Vivo' }
//   ];

//   singleSelectExample = `<app-custom-dropdown
//   [options]="deviceOptions"
//   placeholder="Select"
//   [multiSelect]="false"
//   (selectionChange)="onSelectionChange($event)">
// </app-custom-dropdown>`;

//   multiSelectExample = `<app-custom-dropdown
//   [options]="deviceOptions"
//   placeholder="Select"
//   [multiSelect]="true"
//   (selectionChange)="onSelectionChange($event)">
// </app-custom-dropdown>`;

//   reactiveFormExample = `// In component
// myForm = this.fb.group({
//   devices: [[]]  // for multi-select
// });

// // In template
// <app-custom-dropdown
//   [options]="deviceOptions"
//   [multiSelect]="true"
//   formControlName="devices">
// </app-custom-dropdown>`;

//   onSingleSelectChange(value: any) {
//     this.singleValue = value;
//     console.log('Single select changed:', value);
//   }

//   onMultiSelectChange(values: any[]) {
//     this.multiValues = values;
//     console.log('Multi select changed:', values);
//   }
// }

// export default DropdownDemoComponent;
